import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { formatDate } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  PoChartSerie,
  PoChartType,
  PoPageAction,
  PoSelectOption
} from '@po-ui/ng-components';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import {
  FINDING_KIND_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  categoryLabel,
  leverLabel
} from '../../curation-constants';
import {
  CurationStats,
  CurationStatsBreakdown,
  CurationStatsFilters,
  CurationStatus,
  CurationVoteBreakdown
} from '../../interfaces/curation.interface';
import { SOURCE_PROVIDER_OPTIONS } from 'src/app/menu/smart-review/core/utils/provider-label.util';
import { CurationService } from '../../curation.service';
import { CostsAreasService } from '../../../costs/costs-areas.service';
import { ReviewStatsService } from 'src/app/menu/smart-review/dashboard/review-stats.service';
import { ReviewStatsFilters } from 'src/app/menu/smart-review/dashboard/interfaces/review-stats.interface';
import { RESOURCE_TYPE_FILTER_OPTIONS } from '../../../costs/utils/costs-resource.util';
import { Period, defaultPeriod, rehydratePeriod } from 'src/app/menu/smart-review/core/utils/period.util';
import { withSinglePointTooltip } from 'src/app/menu/smart-review/core/utils/po-chart-tooltip.util';
import { UI_PREF_KEYS, readPref, sanitizeStringArray, writePref } from 'src/app/menu/smart-review/core/utils/ui-prefs.util';
import { getApiErrorMessage } from 'src/app/menu/smart-review/core/utils/http-error-message.util';
import { formatSeverityLabel } from 'src/app/menu/smart-review/review/utils/severity.util';
import { FINDING_RESOLUTIONS } from 'src/app/menu/smart-review/review/review-feedback.constants';
import { FeedbackResolution } from 'src/app/menu/smart-review/review/interfaces/review-feedback.interface';

/** Item simples chave/valor para charts e tabelas do painel. */
interface Slice {
  label: string;
  count: number;
}

interface PrimaryLeverIndicator {
  label: string;
  count: number;
  pct: string;
}

const MAX_FEEDBACK_USERS_IN_CHART = 15;
const MAX_CURATORS_IN_CHART = 15;
const ITEMS_CHART_LABEL = 'Itens';
const QUANTITY_CHART_LABEL = 'Quantidade';
const CURATIONS_CHART_LABEL = 'Curadorias';
const REVIEWS_CHART_LABEL = 'Avaliações';
const PENDING_CHART_LABEL = 'Pendentes';
const FP_RATE_CHART_LABEL = 'Falso positivo %';

type ChartValueFormatter = (value: number) => string;

/** Painel/Dashboard da Curadoria. */
@Component({
  selector: 'app-curation-stats',
  templateUrl: './curation-stats.component.html',
  styleUrls: ['./curation-stats.component.css']
})
export class CurationStatsComponent implements OnInit {
  loading = false;
  stats: CurationStats | null = null;

  // Período: recorta apenas as métricas de fluxo (scopes.period). As de estado
  // (scopes.snapshot) ignoram o período e mostram sempre o estado atual.
  period: Period = defaultPeriod();

  readonly sourceProviderOptions: PoSelectOption[] = SOURCE_PROVIDER_OPTIONS;
  selectedSourceProviders: string[] = [];
  /**
   * Área de quem pediu o review e tipo de review — o mesmo recorte do dashboard de reviews
   * (decisão 0033 do backend). A Calibragem de lá abre este painel já com eles.
   */
  readonly resourceTypeOptions: PoSelectOption[] = RESOURCE_TYPE_FILTER_OPTIONS as PoSelectOption[];
  areaOptions: PoSelectOption[] = [];
  selectedAreas: string[] = [];
  selectedResourceTypes: string[] = [];
  /** Quem pediu o review. Opções vêm do breakdown `user` do dashboard — não há endpoint de usuários. */
  requesterOptions: PoSelectOption[] = [];
  selectedRequesters: string[] = [];
  private requesterSequence = 0;

  readonly donutType = PoChartType.Donut;
  readonly barType = PoChartType.Bar;
  readonly lineType = PoChartType.Line;

  /** Tooltips dos votos — mesmo texto do card de feedback da tela de review. */
  readonly voteTooltips = {
    truePositive:  this.tooltipFor('true_positive'),
    falsePositive: this.tooltipFor('false_positive'),
    wontFix:       this.tooltipFor('wont_fix')
  };

  private tooltipFor(value: FeedbackResolution): string {
    const r = FINDING_RESOLUTIONS.find(o => o.value === value)!;
    return `${r.label} — ${r.hint}`;
  }

  /**
   * Textos de ajuda (ⓘ) dos cards com métrica composta — denominador, recorte de
   * período e a distinção entre "feedback" (voto do dev) e "curadoria" (golden set)
   * não são óbvios. Cards triviais (Pendentes/Curados/Descartados) não recebem tooltip.
   */
  readonly cardHelp = {
    fixablePct:
      'Percentual dos itens curados que são acionáveis (têm lever de melhoria, ou seja, ' +
      'lever ≠ não corrigível). Base: todos os itens curados até hoje — estado atual, não ' +
      'recortado pelo período. Sem itens curados, fica "—".',
    primaryLever:
      'Lever (alavanca de melhoria) mais frequente entre todos os itens curados até hoje ' +
      '(estado atual, não recortado pelo período), com o percentual que ela representa sobre ' +
      'o total de itens curados.',
    fpRate:
      'Percentual dos votos de dev em achados classificados como falso positivo (FP) sobre o ' +
      'total de votos em achados no período. Considera apenas feedback de achado (exclui o ' +
      'veredito geral do review). Cada voto conta (não é deduplicado por achado).',
    fixRate:
      'Dos achados válidos (TP), percentual que será corrigido — ou seja, TP sobre (TP + WF). ' +
      'WF (won\'t fix) são válidos mas que não serão corrigidos.',
    timeToCurate:
      'Tempo entre o primeiro feedback do achado e a sua curadoria. Mostra a média, com a ' +
      'mediana entre parênteses. Considera apenas itens já curados no período.',
    consensus:
      'Dos achados votados por 2 ou mais revisores distintos no período, percentual em que ' +
      'houve maioria estrita entre os revisores (mais da metade dos devs no mesmo veredito, ' +
      'contando um voto por dev). Mede o alinhamento entre revisores.',
    backlogOldest:
      'Há quantos dias está pendente o achado mais antigo da fila de curadoria (com feedback ' +
      'e ainda não curado/descartado).',
    findingsCoverage:
      'Percentual dos achados gerados por reviews concluídos no período que receberam ao ' +
      'menos um voto de dev. "Sem feedback" = achados que nenhum dev avaliou (não entram na ' +
      'fila de curadoria). Feedback é o voto do dev, diferente da curadoria (golden set).',
    reviewsCoverage:
      'Percentual dos reviews concluídos no período que receberam ao menos um voto de dev. ' +
      'Basta um voto em qualquer achado para o review contar como avaliado. "Sem feedback" = ' +
      'reviews sem nenhum voto.',
    verdictDistribution:
      'Distribuição dos votos de dev em achados no período por veredito: procede (TP), falso ' +
      'positivo (FP) e não corrigir (WF). Exclui o veredito geral do review (overall). ' +
      'Cada voto conta (não é deduplicado por achado).',
    goldenBalance:
      'Distribuição dos itens curados (golden set) pelo veredito predominante de cada um: ' +
      'procede (TP), falso positivo (FP) ou não corrigir (WF). Não compara curados × ' +
      'descartados — considera apenas o que entrou no golden set.'
  };

  /**
   * Badge de escopo de um campo do /stats. Lê de `stats.scopes` (não hardcoda):
   * - snapshot → "Estado atual" (ignora o período)
   * - period   → "No período"  (respeita dateFrom/dateTo)
   * Retorna null quando o backend não informou o escopo do campo.
   */
  scopeBadge(field: string): { label: string; period: boolean } | null {
    const scopes = this.stats?.scopes;
    if (!scopes) { return null; }
    if (scopes.snapshot?.includes(field)) { return { label: 'Estado atual', period: false }; }
    if (scopes.period?.includes(field)) { return { label: 'No período', period: true }; }
    return null;
  }

  /** True quando há ao menos um campo marcado como snapshot (período não se aplica a tudo). */
  get hasSnapshotScope(): boolean {
    return (this.stats?.scopes?.snapshot?.length ?? 0) > 0;
  }

  // Funil por status (status -> total).
  funnel: Record<CurationStatus, number> = { pending: 0, curated: 0, discarded: 0 };

  // Donuts / colunas / barras (série + categorias).
  leverCategories: string[] = [];
  leverSeries: PoChartSerie[] = [];
  resourceTypeSeries: PoChartSerie[] = [];
  findingKindSeries: PoChartSerie[] = [];

  categoryCategories: string[] = [];
  categorySeries: PoChartSerie[] = [];

  languageCategories: string[] = [];
  languageSeries: PoChartSerie[] = [];

  curatorCategories: string[] = [];
  curatorSeries: PoChartSerie[] = [];

  feedbackUserCategories: string[] = [];
  feedbackUserSeries: PoChartSerie[] = [];

  overTimeCategories: string[] = [];
  overTimeSeries: PoChartSerie[] = [];

  // Precisão (taxa de falso positivo %) por dimensão — barras.
  fpByLanguageCategories: string[] = [];
  fpByLanguageSeries: PoChartSerie[] = [];
  fpByFindingKindCategories: string[] = [];
  fpByFindingKindSeries: PoChartSerie[] = [];
  fpBySeverityCategories: string[] = [];
  fpBySeveritySeries: PoChartSerie[] = [];
  fpByResourceTypeCategories: string[] = [];
  fpByResourceTypeSeries: PoChartSerie[] = [];

  // Distribuição de vereditos e balanço do golden set — donuts.
  verdictSeries: PoChartSerie[] = [];
  goldenBalanceSeries: PoChartSerie[] = [];

  // Cross-tabs — colunas agrupadas (séries = linguagem).
  leverByLanguageCategories: string[] = [];
  leverByLanguageSeries: PoChartSerie[] = [];
  categoryByLanguageCategories: string[] = [];
  categoryByLanguageSeries: PoChartSerie[] = [];

  // Aging do backlog — barra.
  backlogAgingCategories: string[] = [];
  backlogAgingSeries: PoChartSerie[] = [];

  readonly pageActions: PoPageAction[] = [
    { label: 'Atualizar', icon: 'po-icon-refresh', action: () => this.loadStats() }
  ];

  constructor(
    private curationService: CurationService,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private areasService: CostsAreasService,
    private reviewStatsService: ReviewStatsService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.mainService.validSession();
    // Acesso `smartreview.admin` é garantido pelo SmartReviewAdminGuard na rota pai do hub.
    const saved = readPref(UI_PREF_KEYS.curationStatsFilters, raw => {
      if (!raw || typeof raw !== 'object') { return null; }
      const value = raw as Record<string, unknown>;
      return {
        period: rehydratePeriod(value['period']),
        sourceProviders: sanitizeStringArray(value['sourceProviders'], this.sourceProviderOptions.map(option => String(option.value))),
        resourceTypes: sanitizeStringArray(value['resourceTypes'], this.resourceTypeOptions.map(option => String(option.value))),
        areas: Array.isArray(value['areas']) ? (value['areas'] as unknown[]).filter((a): a is string => typeof a === 'string') : [],
        requesters: Array.isArray(value['requesters']) ? (value['requesters'] as unknown[]).filter((a): a is string => typeof a === 'string') : []
      };
    });
    if (saved?.period) { this.period = saved.period; }
    if (saved?.sourceProviders) { this.selectedSourceProviders = saved.sourceProviders; }
    if (saved?.resourceTypes) { this.selectedResourceTypes = saved.resourceTypes; }
    if (saved?.areas) { this.selectedAreas = saved.areas; }
    if (saved?.requesters) { this.selectedRequesters = saved.requesters; }
    // Vindo da Calibragem do dashboard, o recorte da URL vence o salvo: o painel tem de
    // abrir no mesmo time e período que o card mostrava.
    this.applyQueryFilters(this.route.snapshot.queryParamMap);
    this.areasService.areaNames().subscribe(names => { this.areaOptions = CostsAreasService.toOptions(names); });
    this.refreshRequesterOptions();
    this.loadStats();
  }

  private applyQueryFilters(params: ParamMap): void {
    if (!params.keys.length) { return; }
    const period = rehydratePeriod({
      preset: params.get('period'), dateFrom: params.get('dateFrom'), dateTo: params.get('dateTo')
    });
    if (period) { this.period = period; }
    const providers = params.getAll('sourceProvider');
    if (providers.length) { this.selectedSourceProviders = providers; }
    const areas = params.getAll('area');
    if (areas.length) { this.selectedAreas = areas; }
    const requesters = params.getAll('requestedBy');
    if (requesters.length) { this.selectedRequesters = requesters; }
    const types = params.getAll('resourceType');
    if (types.length) { this.selectedResourceTypes = types; }
  }

  get hasData(): boolean {
    return !!this.stats && (this.stats.total ?? 0) > 0;
  }

  /** % de itens curados acionáveis (qualquer lever ≠ not_fixable). Base: total curado. */
  get fixablePctLabel(): string {
    const total = this.funnel.curated;
    if (!total) { return '—'; }
    const actionable = this.leverBreakdown()
      .filter(slice => slice.key && slice.key !== 'not_fixable')
      .reduce((sum, slice) => sum + slice.count, 0);
    return `${((actionable / total) * 100).toFixed(1)}%`;
  }

  get primaryLeverIndicator(): PrimaryLeverIndicator | null {
    const total = this.funnel.curated;
    if (!total) { return null; }

    const primary = this.leverBreakdown()
      .find(slice => !!slice.key && slice.key !== 'not_fixable');

    if (!primary) { return null; }

    return {
      label: leverLabel(primary.key) || primary.key,
      count: primary.count,
      pct: `${((primary.count / total) * 100).toFixed(1)}%`
    };
  }

  /** Taxa de falso positivo (FP / total de votos) — só quando há `votes`. */
  get fpRateLabel(): string {
    const votes = this.stats?.votes;
    if (!votes || !votes.total) { return ''; }
    return `${((votes.falsePositive / votes.total) * 100).toFixed(1)}%`;
  }

  /**
   * Taxa de correção: dos achados válidos (procede + não corrigir), a fração que
   * será corrigida — TP / (TP + WF). Independe da taxa de FP. Sem válidos = '—'.
   */
  get fixRateLabel(): string {
    const votes = this.stats?.votes;
    if (!votes) { return ''; }
    const valid = votes.truePositive + votes.wontFix;
    if (!valid) { return '—'; }
    return `${((votes.truePositive / valid) * 100).toFixed(1)}%`;
  }

  get hasVotes(): boolean {
    return !!this.stats?.votes && (this.stats.votes.total ?? 0) > 0;
  }

  get feedbackUserChartHeight(): number {
    return Math.max(280, this.feedbackUserCategories.length * 28 + 80);
  }

  get feedbackUserChartTitle(): string {
    return this.feedbackUserCategories.includes('Outros')
      ? `Top ${MAX_FEEDBACK_USERS_IN_CHART} devs por avaliações`
      : 'Avaliações por dev';
  }

  get curatorChartHeight(): number {
    return Math.max(280, this.curatorCategories.length * 28 + 80);
  }

  get curatorChartTitle(): string {
    return this.curatorCategories.includes('Outros')
      ? `Top ${MAX_CURATORS_IN_CHART} curadores por curadorias`
      : 'Curadorias por curador';
  }

  get leverChartHeight(): number {
    return this.barChartHeight(this.leverCategories.length);
  }

  get categoryChartHeight(): number {
    return this.barChartHeight(this.categoryCategories.length);
  }

  get languageChartHeight(): number {
    return this.barChartHeight(this.languageCategories.length);
  }

  get fpByLanguageChartHeight(): number {
    return this.barChartHeight(this.fpByLanguageCategories.length);
  }

  get fpBySeverityChartHeight(): number {
    return this.barChartHeight(this.fpBySeverityCategories.length);
  }

  get fpByFindingKindChartHeight(): number {
    return this.barChartHeight(this.fpByFindingKindCategories.length);
  }

  get fpByResourceTypeChartHeight(): number {
    return this.barChartHeight(this.fpByResourceTypeCategories.length);
  }

  get leverByLanguageChartHeight(): number {
    return this.crossTabChartHeight(this.leverByLanguageCategories.length, this.leverByLanguageSeries.length);
  }

  get categoryByLanguageChartHeight(): number {
    return this.crossTabChartHeight(this.categoryByLanguageCategories.length, this.categoryByLanguageSeries.length);
  }

  /** Altura comum aos gráficos de barra da mesma linha: o mais baixo acompanha o mais alto. */
  rowChartHeight(...heights: number[]): number {
    return Math.max(...heights);
  }

  get backlogAgingChartHeight(): number {
    return this.barChartHeight(this.backlogAgingCategories.length);
  }

  // ─── Indicadores de destaque (cards) ─────────────────────────────────────

  get hasTimeToCurate(): boolean {
    return !!this.stats?.timeToCurateHours;
  }

  /** Latência média formatada (horas; vira dias acima de 48h). */
  get timeToCurateAvgLabel(): string {
    const avg = this.stats?.timeToCurateHours?.avg;
    if (avg == null) { return '—'; }
    return avg >= 48 ? `${(avg / 24).toFixed(1)}d` : `${Math.round(avg)}h`;
  }

  get timeToCurateMedianLabel(): string {
    const median = this.stats?.timeToCurateHours?.median;
    if (median == null) { return '—'; }
    return median >= 48 ? `${(median / 24).toFixed(1)}d` : `${Math.round(median)}h`;
  }

  get hasConsensus(): boolean {
    return this.stats?.consensusRate != null;
  }

  get consensusLabel(): string {
    const rate = this.stats?.consensusRate;
    return rate == null ? '—' : `${(rate * 100).toFixed(0)}%`;
  }

  get hasBacklogAging(): boolean {
    return !!this.stats?.backlogAgingDays?.buckets?.length;
  }

  get backlogOldestLabel(): string {
    const oldest = this.stats?.backlogAgingDays?.oldest;
    return oldest == null ? '—' : `${oldest}d`;
  }

  // ─── Cobertura de feedback ───────────────────────────────────────────────

  get hasCoverage(): boolean {
    return !!this.stats?.coverage;
  }

  get findingsCoverageLabel(): string {
    return this.coveragePct(this.stats?.coverage?.findings);
  }

  get findingsWithoutFeedback(): number {
    return this.stats?.coverage?.findings?.withoutFeedback ?? 0;
  }

  get reviewsCoverageLabel(): string {
    return this.coveragePct(this.stats?.coverage?.reviews);
  }

  get reviewsWithoutFeedback(): number {
    return this.stats?.coverage?.reviews?.withoutFeedback ?? 0;
  }

  private coveragePct(bucket?: { total: number; withFeedback: number }): string {
    if (!bucket || !bucket.total) { return '—'; }
    return `${((bucket.withFeedback / bucket.total) * 100).toFixed(1)}%`;
  }

  /**
   * O período é salvo aqui, e não em `onPeriodChange`: toda carga passa por este ponto,
   * inclusive a do botão "Atualizar". Mesmo desenho da fila e do painel de custos.
   */
  loadStats(): void {
    writePref(UI_PREF_KEYS.curationStatsFilters, {
      period: this.period, sourceProviders: this.selectedSourceProviders,
      areas: this.selectedAreas, requesters: this.selectedRequesters, resourceTypes: this.selectedResourceTypes
    });
    this.loading = true;
    this.curationService.getStats(this.buildStatsFilters()).subscribe({
      next: stats => {
        this.stats = stats;
        this.buildCharts(stats);
        this.buildFunnel(stats);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.stats = null;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao carregar as estatísticas.'), 'error');
      }
    });
  }

  /** Período alterado: recarrega as estatísticas com o novo recorte. */
  onPeriodChange(period: Period): void {
    this.period = period;
    this.refreshRequesterOptions();
    this.loadStats();
  }

  /** Origem alterada: recarrega as estatísticas com o novo recorte. */
  onSourceProviderChange(sourceProviders: string[]): void {
    this.selectedSourceProviders = sourceProviders ?? [];
    this.loadStats();
  }

  /** Área alterada: recarrega as estatísticas com o novo recorte. */
  onAreaChange(areas: string[]): void {
    this.selectedAreas = areas ?? [];
    this.loadStats();
  }

  /** Solicitante alterado: recarrega as estatísticas com o novo recorte. */
  onRequesterChange(requesters: string[]): void {
    this.selectedRequesters = requesters ?? [];
    this.loadStats();
  }

  /**
   * Opções de solicitante do período, pelo breakdown `user` de `/reviews/stats`. Só o
   * período recorta: a lista não encolhe ao escolher área ou tipo, e a chamada — que para
   * admin também calcula duração, série e saúde da fila — roda ao abrir e ao trocar o
   * período, não a cada filtro. Os selecionados entram sempre, para a URL vinda da
   * Calibragem aparecer marcada. Falha aqui não derruba o painel: fica a lista anterior.
   */
  private refreshRequesterOptions(): void {
    const filters: ReviewStatsFilters = { groupBy: ['user'] };
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    const request = ++this.requesterSequence;
    this.reviewStatsService.getStats(filters).pipe(catchError(() => of(null))).subscribe(stats => {
      // Troca rápida de período: a resposta de um período anterior não sobrescreve a atual.
      if (!stats || request !== this.requesterSequence) { return; }
      const keys = (stats.breakdown?.user ?? []).map(bucket => bucket.key).filter(key => !key.startsWith('('));
      const all = Array.from(new Set([...this.selectedRequesters, ...keys]));
      this.requesterOptions = all.map(key => ({ value: key, label: key }));
    });
  }

  /** Tipo de review alterado: recarrega as estatísticas com o novo recorte. */
  onResourceTypeChange(types: string[]): void {
    this.selectedResourceTypes = types ?? [];
    this.loadStats();
  }

  /** Filtros do GET /stats a partir do período/origem atuais. */
  private buildStatsFilters(): CurationStatsFilters {
    const filters: CurationStatsFilters = {};
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    if (this.selectedSourceProviders.length) { filters.sourceProvider = this.selectedSourceProviders; }
    if (this.selectedAreas.length) { filters.area = this.selectedAreas; }
    if (this.selectedRequesters.length) { filters.requestedBy = this.selectedRequesters; }
    if (this.selectedResourceTypes.length) { filters.resourceType = this.selectedResourceTypes; }
    return filters;
  }

  // ─── Funil ──────────────────────────────────────────────────────────────

  /** Usa os contadores do /stats; se ausentes, busca os totais por status na fila. */
  private buildFunnel(stats: CurationStats): void {
    if (stats.pending != null || stats.curated != null || stats.discarded != null) {
      this.funnel = {
        pending: stats.pending ?? 0,
        curated: stats.curated ?? 0,
        discarded: stats.discarded ?? 0
      };
      return;
    }

    const count$ = (status: CurationStatus) => {
      const params = { status, limit: '1', offset: '0' } as Parameters<CurationService['getQueue']>[0];
      if (this.period.dateFrom) { (params as { dateFrom?: string }).dateFrom = this.period.dateFrom; }
      if (this.period.dateTo) { (params as { dateTo?: string }).dateTo = this.period.dateTo; }
      if (this.selectedSourceProviders.length) { (params as { sourceProvider?: string[] }).sourceProvider = this.selectedSourceProviders; }
      return this.curationService.getQueue(params).pipe(catchError(() => of({ total: 0, items: [] })));
    };

    forkJoin({
      pending: count$('pending'),
      curated: count$('curated'),
      discarded: count$('discarded')
    }).subscribe(res => {
      this.funnel = {
        pending: res.pending?.total ?? 0,
        curated: res.curated?.total ?? 0,
        discarded: res.discarded?.total ?? 0
      };
    });
  }

  // ─── Charts ─────────────────────────────────────────────────────────────

  private buildCharts(stats: CurationStats): void {
    // Barra por lever (sem lever = correct/good → "Não acionável").
    const lever = this.toSlices(this.leverBreakdown(), key => leverLabel(key) || 'Não acionável');
    this.leverCategories = lever.map(s => s.label);
    this.leverSeries = this.countSeries(ITEMS_CHART_LABEL, lever.map(s => s.count), true);

    // Coluna por categoria.
    const category = this.toSlices(
      this.sortBreakdown(stats.byCategory),
      key => categoryLabel(key) || key
    );
    this.categoryCategories = category.map(s => s.label);
    this.categorySeries = this.countSeries(QUANTITY_CHART_LABEL, category.map(s => s.count));

    // Barra por linguagem — exclui itens sem linguagem (ex.: review overall).
    const languageBreakdown = this.sortBreakdown(stats.byLanguage).filter(slice => !!slice.key);
    const language = this.toSlices(languageBreakdown, key => key);
    this.languageCategories = language.map(s => s.label);
    this.languageSeries = this.countSeries(QUANTITY_CHART_LABEL, language.map(s => s.count));

    // Donut por tipo de review (resourceType).
    const resourceType = this.toSlices(
      this.sortBreakdown(stats.byResourceType),
      key => this.optionLabel(RESOURCE_TYPE_OPTIONS, key)
    );
    this.resourceTypeSeries = this.donutSeries(resourceType);

    // Donut por tipo de achado (findingKind).
    const findingKind = this.toSlices(
      this.sortBreakdown(stats.byFindingKind),
      key => this.optionLabel(FINDING_KIND_OPTIONS, key)
    );
    this.findingKindSeries = this.donutSeries(findingKind);

    // Barra por curador.
    const curator = this.limitSlices(
      this.toSlices(this.sortBreakdown(stats.byCurator), key => key),
      MAX_CURATORS_IN_CHART,
      'Outros'
    );
    this.curatorCategories = curator.map(s => s.label);
    this.curatorSeries = this.countSeries(CURATIONS_CHART_LABEL, curator.map(s => s.count));

    // Barra por dev avaliador.
    const feedbackUser = this.limitSlices(
      this.toSlices(this.sortBreakdown(stats.byFeedbackUser), key => key),
      MAX_FEEDBACK_USERS_IN_CHART,
      'Outros'
    );
    this.feedbackUserCategories = feedbackUser.map(s => s.label);
    this.feedbackUserSeries = this.countSeries(REVIEWS_CHART_LABEL, feedbackUser.map(s => s.count));

    // Linha — curadorias ao longo do tempo (mantém a ordem cronológica do backend).
    const overTime = (stats.overTime ?? []).filter(point => !!point.date);
    this.overTimeCategories = overTime.map(point =>
      formatDate(point.date, 'dd/MM/yyyy', 'pt-BR')
    );
    this.overTimeSeries = overTime.length
      ? this.countSeries(CURATIONS_CHART_LABEL, overTime.map(point => point.count ?? 0), true)
      : [];

    this.buildPrecisionCharts(stats);
    this.buildGoldenCharts(stats);
    this.buildCrossTabs(stats);
    this.buildBacklogAging(stats);
  }

  /** Taxa de falso positivo (%) por linguagem / tipo de achado / severidade / tipo de review. */
  private buildPrecisionCharts(stats: CurationStats): void {
    const language = this.fpRateChart(stats.votesByLanguage, key => key);
    this.fpByLanguageCategories = language.categories;
    this.fpByLanguageSeries = language.series;

    const findingKind = this.fpRateChart(stats.votesByFindingKind, key => this.optionLabel(FINDING_KIND_OPTIONS, key));
    this.fpByFindingKindCategories = findingKind.categories;
    this.fpByFindingKindSeries = findingKind.series;

    const severity = this.fpRateChart(stats.votesBySeverity, key => formatSeverityLabel(key) || key);
    this.fpBySeverityCategories = severity.categories;
    this.fpBySeveritySeries = severity.series;

    const resourceType = this.fpRateChart(stats.votesByResourceType, key => this.optionLabel(RESOURCE_TYPE_OPTIONS, key));
    this.fpByResourceTypeCategories = resourceType.categories;
    this.fpByResourceTypeSeries = resourceType.series;
  }

  /** Donut de vereditos (todos os votos) e balanço do golden set (curados). */
  private buildGoldenCharts(stats: CurationStats): void {
    const votes = stats.votes;
    this.verdictSeries = votes && votes.total
      ? this.donutSeries([
          { label: 'Procede', count: votes.truePositive },
          { label: 'Falso positivo', count: votes.falsePositive },
          { label: 'Não corrigir', count: votes.wontFix }
        ])
      : [];

    const balance = stats.goldenBalance;
    this.goldenBalanceSeries = balance
      ? this.donutSeries([
          { label: 'Procede', count: balance.truePositive },
          { label: 'Falso positivo', count: balance.falsePositive },
          { label: 'Não corrigir', count: balance.wontFix }
        ])
      : [];
  }

  /** Cross-tabs lever × linguagem e categoria × linguagem (barras agrupadas por linguagem). */
  private buildCrossTabs(stats: CurationStats): void {
    const lever = this.crossTab(stats.leverByLanguage, c => c.lever, c => c.language, key => leverLabel(key) || key);
    this.leverByLanguageCategories = lever.categories;
    this.leverByLanguageSeries = lever.series;

    const category = this.crossTab(stats.categoryByLanguage, c => c.category, c => c.language, key => categoryLabel(key) || key);
    this.categoryByLanguageCategories = category.categories;
    this.categoryByLanguageSeries = category.series;
  }

  private buildBacklogAging(stats: CurationStats): void {
    const buckets = stats.backlogAgingDays?.buckets ?? [];
    this.backlogAgingCategories = buckets.map(b => b.label);
    this.backlogAgingSeries = this.countSeries(PENDING_CHART_LABEL, buckets.map(b => b.count ?? 0), true);
  }

  private leverBreakdown(): CurationStatsBreakdown[] {
    return this.sortBreakdown(this.stats?.byLever);
  }

  /** Cópia ordenada (desc por contagem) de um breakdown do backend. */
  private sortBreakdown(breakdown: CurationStatsBreakdown[] | undefined): CurationStatsBreakdown[] {
    return [...(breakdown ?? [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  }

  private toSlices(breakdown: CurationStatsBreakdown[], labelFn: (key: string) => string): Slice[] {
    return breakdown.map(item => ({ label: labelFn(item.key), count: item.count }));
  }

  private limitSlices(slices: Slice[], maxItems: number, othersLabel: string): Slice[] {
    if (slices.length <= maxItems) { return slices; }

    const visible = slices.slice(0, maxItems);
    const others = slices.slice(maxItems).reduce((sum, slice) => sum + slice.count, 0);
    return others > 0 ? [...visible, { label: othersLabel, count: others }] : visible;
  }

  private barChartHeight(categories: number): number {
    return Math.max(280, categories * 36 + 100);
  }

  private crossTabChartHeight(categories: number, series: number): number {
    const rowHeight = Math.max(36, series * 18);
    return Math.max(300, categories * rowHeight + 100);
  }

  private optionLabel(options: { value: string | number; label: string }[], key: string): string {
    return options.find(option => option.value === key)?.label || key;
  }

  /** Série única de taxa de FP (%) por chave, ordenada desc. */
  private fpRateChart(
    breakdown: CurationVoteBreakdown[] | undefined,
    labelFn: (key: string) => string
  ): { categories: string[]; series: PoChartSerie[] } {
    const list = (breakdown ?? [])
      .filter(item => (item.total ?? 0) > 0)
      // falsePositive pode vir ausente/null do backend; ?? 0 evita rate = NaN.
      .map(item => ({ label: labelFn(item.key), rate: Math.round(((item.falsePositive ?? 0) / item.total) * 1000) / 10 }))
      .sort((a, b) => b.rate - a.rate);
    return {
      categories: list.map(item => item.label),
      series: list.length ? this.countSeries(FP_RATE_CHART_LABEL, list.map(item => item.rate), true, formatPercentValue) : []
    };
  }

  /** Cross-tab genérico → colunas agrupadas (linhas = eixo X, colunas = séries). */
  private crossTab<T>(
    cells: T[] | undefined,
    getRow: (cell: T) => string,
    getCol: (cell: T) => string,
    rowLabel: (key: string) => string
  ): { categories: string[]; series: PoChartSerie[] } {
    if (!cells?.length) { return { categories: [], series: [] }; }

    const rows: string[] = [];
    const cols: string[] = [];
    const matrix = new Map<string, number>();
    for (const cell of cells) {
      const row = getRow(cell);
      const col = getCol(cell);
      if (!rows.includes(row)) { rows.push(row); }
      if (col && !cols.includes(col)) { cols.push(col); }
      const mapKey = `${row}|${col}`;
      matrix.set(mapKey, (matrix.get(mapKey) ?? 0) + ((cell as { count?: number }).count ?? 0));
    }

    return {
      categories: rows.map(rowLabel),
      series: cols.map(col => this.withFormattedSinglePointTooltip({
        label: col,
        data: rows.map(row => matrix.get(`${row}|${col}`) ?? 0)
      }))
    };
  }

  private countSeries(
    label: string,
    values: number[],
    emptyWhenNoValues = false,
    formatter: ChartValueFormatter = formatCountValue
  ): PoChartSerie[] {
    if (emptyWhenNoValues && !values.length) { return []; }
    return [this.withFormattedSinglePointTooltip({ label, data: values }, formatter)];
  }

  private donutSeries(slices: Slice[]): PoChartSerie[] {
    const total = slices.reduce((sum, slice) => sum + slice.count, 0);
    return slices.map(slice => ({
      label: slice.label,
      data: slice.count,
      tooltip: `${slice.label}: ${formatCountShare(slice.count, total)}`
    }));
  }

  private withFormattedSinglePointTooltip(serie: PoChartSerie, formatter: ChartValueFormatter = formatCountValue): PoChartSerie {
    const data = serie.data;
    if (!Array.isArray(data) || data.length !== 1) { return serie; }
    return withSinglePointTooltip(serie, formatter(data[0]));
  }
}

function formatCountValue(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('pt-BR') : '0';
}

function formatPercentValue(value: number): string {
  return Number.isFinite(value) ? `${value.toLocaleString('pt-BR')}%` : '0%';
}

function formatCountShare(value: number, total: number): string {
  const count = formatCountValue(value);
  if (!total) { return count; }
  return `${count} (${formatSharePercent(value, total)})`;
}

function formatSharePercent(value: number, total: number): string {
  const rounded = Math.round((value / total) * 1000) / 10;
  return `${rounded.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}
